import { Component, Injector, OnInit, ViewContainerRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import {
    ApiRoutes,
    TitleConstants,
    UiRouteLinks,
    UiRoutes
} from '@lsq-frontend/angular-shared/app-constants';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { GenericHealthService } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { ResponseCode } from '@lsq-frontend/remote-services/enterprise/common';
import { ClassNames } from 'libs/common-ui/src/constants/class-names.constants';
import { Modal } from 'libs/common-ui/src/models/common';
import {
    Announcement,
    AnnouncementService
} from 'libs/common-ui/src/services/announcement/announcement.service';
import { ClaimService } from 'libs/common-ui/src/services/claim/claim.service';
import { DetectBrowserService } from 'libs/common-ui/src/services/detect-browser/detect-browser.service';
import { FeatureFlagService } from 'libs/common-ui/src/services/feature-flag/feature-flag.service';
import { FloatingActionButtonService } from 'libs/common-ui/src/services/floating-action-button/floating-action-button.service';
import { HideOpenElementsService } from 'libs/common-ui/src/services/hide-open-elements/hide-open-elements.service';
import { IdleService } from 'libs/common-ui/src/services/idle/idle.service';
import { ModalService } from 'libs/common-ui/src/services/modal/modal.service';
import { ResponsiveService } from 'libs/common-ui/src/services/responsive/responsive.service';
import { AuthenticationService } from 'libs/common-ui/src/services/user/authentication.service';
import { WindowReferenceService } from 'libs/common-ui/src/services/window-reference/window-reference.service';
import { WorkflowMonitorService } from 'libs/common-ui/src/services/workflow-monitor/workflow-monitor.service';
import { getDeepestFirstChild } from 'libs/common-ui/src/utilities/route.helpers';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, skipWhile, startWith, timeout } from 'rxjs/operators';
import { LsqSessionIdleModalComponent } from '../../modals/session-idle-modal.component';

@Component({
    selector: 'lsq-app-root',
    template: ''
})
export class LsqAppRootComponent implements OnInit {
    public containerClass = '';
    public isApiHealthy: boolean;
    public topContentAnnouncementClass = '';
    public topContentGlobalSearchClass = '';
    public idleModalIsOpen = false;
    public idleIsActive = false;
    public isFabAllowedStream: Observable<boolean>;
    public canBypassHealthCheck$: Observable<boolean>;
    public isWaitingForHealthAndUser = true;

    // Injections.
    public userService: UserService;
    protected alertService: AlertService;
    protected analyticsService: AnalyticsService;
    protected announcementService: AnnouncementService;
    protected claimService: ClaimService;
    protected healthService: GenericHealthService;
    protected featureFlagService: FeatureFlagService;
    protected titleService: Title;
    protected windowReferenceService: WindowReferenceService;
    protected responsiveService: ResponsiveService;
    protected router: Router;
    protected route: ActivatedRoute;
    protected toastrService: ToastrService;
    protected deviceDetectorService: DeviceDetectorService;
    protected detectBrowserService: DetectBrowserService;
    protected idleService: IdleService;
    protected modalService: ModalService;
    protected settings: ISettings;
    protected hideOpenElementsService: HideOpenElementsService;
    protected workflowMonitorService: WorkflowMonitorService;
    protected floatingActionButtonService: FloatingActionButtonService;
    protected errorToastBlacklistService: ErrorToastBlacklistService;
    protected authenticationService: AuthenticationService;

    constructor(
        public injector: Injector,
        public viewContainerRef: ViewContainerRef,
        private _router: Router
    ) {
        // Services
        this.analyticsService = this.injector.get(AnalyticsService);
        this.announcementService = this.injector.get(AnnouncementService);
        this.claimService = this.injector.get(ClaimService);
        this.featureFlagService = this.injector.get(FeatureFlagService);
        this.healthService = this.injector.get(GenericHealthService);
        this.titleService = this.injector.get(Title);
        this.alertService = this.injector.get(AlertService);
        this.userService = this.injector.get(UserService);
        this.windowReferenceService = this.injector.get(WindowReferenceService);
        this.responsiveService = this.injector.get(ResponsiveService);
        this.deviceDetectorService = this.injector.get(DeviceDetectorService);
        this.detectBrowserService = this.injector.get(DetectBrowserService);
        this.modalService = this.injector.get(ModalService);
        this.settings = this.injector.get(SETTINGS);
        this.hideOpenElementsService = this.injector.get(HideOpenElementsService);
        this.workflowMonitorService = this.injector.get(WorkflowMonitorService);
        this.floatingActionButtonService = this.injector.get(FloatingActionButtonService);
        this.idleService = this.injector.get(IdleService);
        this.router = this.injector.get(Router);
        this.route = this.injector.get(ActivatedRoute);
        this.toastrService = this.injector.get(ToastrService);
        this.errorToastBlacklistService = this.injector.get(ErrorToastBlacklistService);
        this.authenticationService = this.injector.get(AuthenticationService);

        this.userService.logout$.subscribe(() => this.checkApiHealth(false));

        this.userService.user$.pipe(filter((maybeUser) => !maybeUser)).subscribe(() => {
            if (this.authenticationService.isOn()) {
                this.authenticationService.clearSession();
            }
        });

        this.isFabAllowedStream = this.floatingActionButtonService.isAllowedStream;

        this.canBypassHealthCheck$ = this._router.events.pipe(
            filter((event: RouterEvent) => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.url === UiRouteLinks.Health),
            startWith(false)
        );
    }

    public ngOnInit(): void {
        this.errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${ApiRoutes.Api}/${ApiRoutes.Health}`,
            ResponseCode.Error
        );
        this.errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${ApiRoutes.Api}/${ApiRoutes.Health}`,
            503
        );

        //Subscribe the template flags to the health service
        this.healthService.isApiHealthy$.subscribe((value) => {
            this.isApiHealthy = value;
        });
        this.healthService.isWaitingForHealthAndUser$.subscribe((value) => {
            this.isWaitingForHealthAndUser = value;
        });
        // Get the health of Tier 1 and the current user from the server.
        if (!this.healthService.isExecuting && !this.isApiHealthy) {
            this.checkApiHealth();
        }

        // On route change:
        // - apply any custom container css for routes
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
            this._handleRouteChange();
        });

        this.analyticsService.initalize();

        if (this.viewContainerRef) {
            const overlayContainer = new ToastContainerDirective(this.viewContainerRef.element);
            this.toastrService.overlayContainer = overlayContainer;
        } else {
            console.warn('Please inject ViewContainerRef in your AppComponent');
        }

        this.alertService.successStream.subscribe((params) => {
            this.toastrService.success(params.message, params.title, params.config);
        });

        this.alertService.infoStream.subscribe((params) => {
            this.toastrService.info(params.message, params.title, params.config);
        });

        this.alertService.errorStream.subscribe((params) => {
            this.toastrService.error(params.message, params.title, params.config);
        });

        this.announcementService.announcementsStream.subscribe((value: Announcement[]) => {
            this.topContentAnnouncementClass = ClassNames.TopContent + value.length;
        });

        if ((window as any).Modernizr?.touchevents) {
            document.body.classList.add('is-touch-device');
        }
    }

    public async handleIdleServiceCountdownFinished(): Promise<void> {
        // Force logging out the user because they have timed out.
        await this.router.navigateByUrl(UiRouteLinks.Logout);
        this.modalService.closeModal();
        this.idleModalIsOpen = false;
    }

    public checkApiHealth(fetchUser = true): void {
        combineLatest([
            this.healthService.dataStream.pipe(startWith(null)),
            this.healthService.errorStream.pipe(startWith(null))
        ])
            .pipe(
                timeout(this.settings.healthCheckTimeoutMs),
                skipWhile(([healthSuccess, healthError]) => !healthSuccess && !healthError),
                first()
            )
            .subscribe(
                async ([healthSuccess, healthError]) => {
                    this.healthService.isApiHealthy$.next(!!healthSuccess && !healthError);

                    if (healthSuccess && fetchUser) {
                        combineLatest([
                            this.userService.user$.pipe(startWith(null)),
                            this.userService.error$.pipe(startWith(null))
                        ])
                            .pipe(
                                skipWhile(([user, userError]) => !user && !userError),
                                first()
                            )
                            .subscribe((_) => {
                                this.healthService.isWaitingForHealthAndUser$.next(false);
                            });

                        await this.userService.getAndEmitCurrentUser();
                    } else {
                        this.healthService.isWaitingForHealthAndUser$.next(false);
                    }
                },
                () => {
                    this.healthService.isWaitingForHealthAndUser$.next(false);
                    this.healthService.isApiHealthy$.next(false);
                }
            );
        this.healthService.execute();
    }

    public displaySessionIdleModal(): void {
        if (!this.idleModalIsOpen) {
            this.hideOpenElementsService.hideOpenElements();
            this.idleModalIsOpen = true;
            this.modalService.showModal(
                new Modal({
                    id: 'session-idle-modal',
                    title: 'Are you still there?',
                    bodyComponent: LsqSessionIdleModalComponent,
                    clickOutsideToClose: false,
                    showFooter: true,
                    showOkButton: true,
                    okButtonText: 'Continue',
                    onOkClick: () => {
                        this.modalService.closeModal();
                        this.idleModalIsOpen = false;
                        this.idleService.reset();
                    },
                    showCancelButton: true,
                    cancelButtonText: 'Finish Later',
                    onCancelClick: () => {
                        this.workflowMonitorService.markWorkflowCancelled();
                        this.modalService.closeModal();
                        this.idleModalIsOpen = false;
                        this.router.navigateByUrl(UiRouteLinks.Logout);
                    }
                })
            );
        }
    }

    /**
     * Creates an analytics key for the current route by concatenating its `Route.path`
     * with all its ancestors, so that route params remain unresolved (as opposed to
     * `Router#url`, which would give us the resolved path).
     */
    private _buildPathForAnalytics(): string {
        let childRoute = this.route;
        let path = '';
        while (childRoute) {
            if (childRoute.routeConfig && childRoute.routeConfig.path) {
                path = `${path}/${childRoute.routeConfig.path}`;
            }
            childRoute = childRoute.firstChild;
        }
        return path ? path : `/${UiRoutes.Home}`;
    }

    private _handleRouteChange(): void {
        if (this.router.config.length === 0) {
            return;
        }
        const deepestChild = getDeepestFirstChild(this.route.snapshot);
        if (deepestChild) {
            const data = deepestChild.data;
            if (data.externalRedirectTo) {
                this.windowReferenceService.goToExternalPage(data.externalRedirectTo);
                return;
            }

            let title = '';
            if (data.title) {
                title = `${data.title}${TitleConstants.CoreSiteTitleSuffix}`;
            } else if (data.navigationLabel) {
                title = `${data.navigationLabel}${TitleConstants.CoreSiteTitleSuffix}`;
            } else {
                title = TitleConstants.CoreSiteTitle;
            }
            this.containerClass = data.containerClass;
            this.titleService.setTitle(title);
            this.analyticsService.setPageView(this._buildPathForAnalytics(), title);
        }
    }
}
